/**
 * Enhanced Notifications Page
 * Uses SaaSFoundation's notification API
 * Includes: Bulk delete, statistics, pagination
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Check, X, AlertCircle, Info, CheckCircle, XCircle,
  Settings, Filter, Search, RefreshCw, Loader, Trash2,
  BarChart3, CheckSquare, Square, ArrowRight
} from 'lucide-react';
import { useNotification } from '../agenticsaas/contexts/NotificationContext';
import { notificationService } from '../services/notificationService';

const Notifications = () => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh,
  } = useNotification();

  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [showSettings, setShowSettings] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    email: true,
    push: true,
    sms: false,
    weekly_digest: true,
  });
  const [preferencesLoading, setPreferencesLoading] = useState(false);

  // Fetch preferences
  const fetchPreferences = useCallback(async () => {
    try {
      setPreferencesLoading(true);
      const data = await notificationService.getPreferences();
      if (data) {
        setPreferences({
          email: data.email ?? true,
          push: data.push ?? true,
          sms: data.sms ?? false,
          weekly_digest: data.weekly_digest ?? true,
        });
      }
    } catch (err) {
      console.error('Failed to fetch preferences:', err);
      // Use default preferences on error
      setPreferences({
        email: true,
        push: true,
        sms: false,
        weekly_digest: true,
      });
    } finally {
      setPreferencesLoading(false);
    }
  }, []);

  // Update preferences
  const updatePreferences = useCallback(async (newPreferences) => {
    try {
      setPreferencesLoading(true);
      await notificationService.updatePreferences(newPreferences);
      setPreferences(prev => ({ ...prev, ...newPreferences }));
    } catch (err) {
      console.error('Failed to update preferences:', err);
    } finally {
      setPreferencesLoading(false);
    }
  }, []);

  // Load preferences on mount
  React.useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  // Filter notifications locally
  const filteredNotifications = useMemo(() => {
    return notifications.filter(notification => {
      const matchesFilter = 
        filter === 'all' ||
        (filter === 'unread' && !notification.read) ||
        (filter === 'read' && notification.read) ||
        notification.category === filter;
      
      const matchesSearch = 
        searchTerm === '' ||
        notification.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notification.message?.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesFilter && matchesSearch;
    });
  }, [notifications, filter, searchTerm]);

  // Update filters and refresh notifications when filters change
  React.useEffect(() => {
    // Only refresh if refresh function is available
    if (!refresh || typeof refresh !== 'function') {
      return;
    }
    
    const timeoutId = setTimeout(() => {
      // Refresh notifications with current filters
      refresh();
    }, 300); // Debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filter, refresh]);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning': return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'error': return <XCircle className="w-5 h-5 text-red-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const handleMarkAsRead = async (id) => {
    try {
      await markAsRead(id);
    } catch (err) {
      // Error handled in context
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (err) {
      // Error handled in context
    }
  };

  const handleDeleteNotification = async (id) => {
    try {
      await deleteNotification(id);
    } catch (err) {
      // Error handled in context
    }
  };

  const handleViewDetails = async (notification) => {
    if (!notification.read) {
      try {
        await markAsRead(notification.id);
      } catch (err) {
        // Error handled in context
      }
    }
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  const togglePreference = (key) => {
    const newValue = !preferences[key];
    updatePreferences({ [key]: newValue });
  };

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const data = await notificationService.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Toggle notification selection
  const toggleSelection = (notificationId) => {
    setSelectedNotifications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  // Select all visible notifications
  const selectAll = () => {
    setSelectedNotifications(new Set(filteredNotifications.map(n => n.id)));
  };

  // Deselect all
  const deselectAll = () => {
    setSelectedNotifications(new Set());
  };

  // Bulk delete selected notifications
  const handleBulkDelete = async () => {
    if (selectedNotifications.size === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedNotifications.size} notification(s)?`)) {
      return;
    }

    setBulkDeleteLoading(true);
    try {
      await notificationService.deleteMultipleNotifications(Array.from(selectedNotifications));
      setSelectedNotifications(new Set());
      refresh();
    } catch (err) {
      console.error('Failed to delete notifications:', err);
      alert('Failed to delete notifications. Please try again.');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // Load stats when stats panel is opened
  useEffect(() => {
    if (showStats && !stats) {
      fetchStats();
    }
  }, [showStats, stats, fetchStats]);

  // Detect if running in iframe
  const isInIframe = typeof window !== 'undefined' && window.self !== window.top;

  if (loading && notifications.length === 0) {
    return (
      <div className={isInIframe ? "w-full p-2 bg-gray-50 min-h-screen" : "w-full p-4 bg-gray-50 min-h-screen"}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-center">
            <Loader className="w-8 h-8 animate-spin text-blue-500" />
            <span className="ml-2 text-gray-600">Loading notifications...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={isInIframe ? "w-full p-2 bg-gray-50 min-h-screen" : "w-full p-4 bg-gray-50 min-h-screen"}>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Error Display */}
        {error && (
          <div className={isInIframe ? "px-2 py-2 bg-red-50 border-b border-red-200" : "px-4 py-3 bg-red-50 border-b border-red-200"}>
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-700">Error loading notifications: {error}</span>
              <button
                onClick={refresh}
                className="ml-auto text-red-600 hover:text-red-800 underline"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className={isInIframe ? "px-2 py-2 border-b border-gray-200 bg-gray-50 rounded-t-lg" : "px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg"}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Bell className="w-6 h-6 text-gray-700" />
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </div>
              <h1 className="text-xl font-semibold text-gray-900">Notifications</h1>
              {unreadCount > 0 && (
                <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={refresh}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                title="Refresh notifications"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Mark all as read
                </button>
              )}
              <button
                onClick={() => setShowStats(!showStats)}
                className={`p-2 rounded-lg transition-colors ${
                  showStats 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
                title="View statistics"
              >
                <BarChart3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className={isInIframe ? "px-2 py-2 border-b border-gray-200 space-y-3" : "px-4 py-3 border-b border-gray-200 space-y-4"}>
          <div className="flex items-center space-x-4">
            <div className={isInIframe ? "relative flex-1 max-w-sm" : "relative flex-1 max-w-md"}>
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
                <option value="denials">Denials</option>
                <option value="claims">Claims</option>
                <option value="ar-posting">AR Posting</option>
                <option value="prior-auth">Prior Auth</option>
                <option value="insurance">Insurance</option>
                <option value="analytics">Analytics</option>
                <option value="work-queue">Work Queue</option>
                <option value="clearinghouse">EDI Platform</option>
                <option value="charges">Charges</option>
                <option value="billing">Billing</option>
                <option value="team">Team</option>
                <option value="system">System</option>
                <option value="security">Security</option>
              </select>
            </div>
          </div>
        </div>

        {/* Statistics Panel */}
        {showStats && (
          <div className={isInIframe ? "px-2 py-2 border-b border-gray-200 bg-gray-50" : "px-4 py-3 border-b border-gray-200 bg-gray-50"}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900">Notification Statistics</h3>
              <button
                onClick={fetchStats}
                disabled={statsLoading}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                {statsLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
            {statsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader className="w-5 h-5 animate-spin text-blue-500" />
              </div>
            ) : stats ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-gray-900">{stats.total || 0}</div>
                  <div className="text-xs text-gray-600 mt-1">Total</div>
                </div>
                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-red-600">{stats.unread || 0}</div>
                  <div className="text-xs text-gray-600 mt-1">Unread</div>
                </div>
                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-green-600">
                    {stats.total ? Math.round(((stats.total - (stats.unread || 0)) / stats.total) * 100) : 0}%
                  </div>
                  <div className="text-xs text-gray-600 mt-1">Read Rate</div>
                </div>
                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-blue-600">
                    {stats.recent_activity?.last_7_days || 0}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">Last 7 Days</div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No statistics available</p>
            )}
          </div>
        )}

        {/* Settings Panel */}
        {showSettings && (
          <div className={isInIframe ? "px-2 py-2 border-b border-gray-200 bg-gray-50" : "px-4 py-3 border-b border-gray-200 bg-gray-50"}>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Notification Preferences</h3>
            <div className="space-y-2">
              {[
                { key: 'email', label: 'Email notifications' },
                { key: 'push', label: 'Push notifications' },
                { key: 'sms', label: 'SMS notifications' },
                { key: 'weekly_digest', label: 'Weekly digest' }
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={preferences[key] || false}
                    onChange={() => togglePreference(key)}
                    disabled={preferencesLoading}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">{label}</span>
                  {preferencesLoading && (
                    <Loader className="ml-2 w-3 h-3 animate-spin text-blue-500" />
                  )}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Bulk Actions Bar */}
        {selectedNotifications.size > 0 && (
          <div className={isInIframe ? "px-2 py-2 border-b border-gray-200 bg-blue-50" : "px-4 py-3 border-b border-gray-200 bg-blue-50"}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-900">
                  {selectedNotifications.size} notification{selectedNotifications.size !== 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={deselectAll}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Deselect all
                </button>
              </div>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleteLoading}
                className="flex items-center space-x-2 px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
              >
                <Trash2 className="w-4 h-4" />
                <span>{bulkDeleteLoading ? 'Deleting...' : 'Delete Selected'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Notifications List */}
        <div className="divide-y divide-gray-200">
          {filteredNotifications.length === 0 ? (
            <div className={isInIframe ? "px-2 py-6 text-center" : "px-4 py-8 text-center"}>
              <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {searchTerm || filter !== 'all' ? 'No notifications match your criteria' : 'No notifications found'}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-2 text-blue-600 hover:text-blue-800 text-sm underline"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`${isInIframe ? "px-2 py-2" : "px-4 py-3"} hover:bg-gray-50 transition-colors ${
                  !notification.read ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                } ${selectedNotifications.has(notification.id) ? 'bg-blue-100' : ''}`}
              >
                <div className="flex items-start space-x-3">
                  {/* Selection Checkbox */}
                  <div className="flex-shrink-0 mt-1">
                    <button
                      onClick={() => toggleSelection(notification.id)}
                      className="p-1 hover:bg-gray-200 rounded transition-colors"
                      title={selectedNotifications.has(notification.id) ? 'Deselect' : 'Select'}
                    >
                      {selectedNotifications.has(notification.id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  
                  <div className="flex-shrink-0 mt-1">
                    {getTypeIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${
                          !notification.read ? 'text-gray-900' : 'text-gray-700'
                        }`}>
                          {notification.title}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          {notification.message}
                        </p>
                        <div className="flex items-center space-x-3 mt-2">
                          <span className="text-xs text-gray-500">
                            {formatTimeAgo(notification.created_at)}
                          </span>
                          {notification.category && (
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              notification.category === 'billing' ? 'bg-green-100 text-green-800' :
                              notification.category === 'team' ? 'bg-blue-100 text-blue-800' :
                              notification.category === 'system' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {notification.category}
                            </span>
                          )}
                          {notification.action_url && (
                            <button
                              onClick={() => handleViewDetails(notification)}
                              className="flex items-center text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                              View details
                              <ArrowRight className="w-3 h-3 ml-0.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        {!notification.read && (
                          <button
                            onClick={() => handleMarkAsRead(notification.id)}
                            className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                            title="Mark as read"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteNotification(notification.id)}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete notification"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className={isInIframe ? "px-2 py-2 bg-gray-50 rounded-b-lg border-t border-gray-200" : "px-4 py-3 bg-gray-50 rounded-b-lg border-t border-gray-200"}>
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Showing {filteredNotifications.length} of {notifications.length} notifications
            </p>
            {filteredNotifications.length > 0 && (
              <button
                onClick={selectedNotifications.size === filteredNotifications.length ? deselectAll : selectAll}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                {selectedNotifications.size === filteredNotifications.length ? 'Deselect all' : 'Select all'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;

